import { useEffect, useState, useCallback, type ReactNode } from 'react';
import type { PublicUser } from '@nfa/types';
import * as api from '../lib/api';
import { AuthContext, type AuthContextValue } from './authTypes';

const SESSION_TOKEN_KEY = 'nfa_session_token';

interface AuthState {
  user: PublicUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Get session token from localStorage
  const getSessionToken = useCallback((): string | null => {
    try {
      return localStorage.getItem(SESSION_TOKEN_KEY);
    } catch {
      return null;
    }
  }, []);

  // Set session token in localStorage
  const setSessionToken = useCallback((token: string | null) => {
    try {
      if (token) {
        localStorage.setItem(SESSION_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Load user on mount if session token exists
  useEffect(() => {
    const loadUser = async () => {
      const token = getSessionToken();
      if (!token) {
        setState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      try {
        const { user } = await api.getMe(token);
        setState({ user, isLoading: false, isAuthenticated: true });
      } catch {
        // Session expired or invalid - clear it
        setSessionToken(null);
        setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    };

    loadUser();
  }, [getSessionToken, setSessionToken]);

  // Request magic link
  const login = useCallback(async (email: string) => {
    const result = await api.login(email);
    return result;
  }, []);

  // Verify magic link token
  const verifyToken = useCallback(async (token: string) => {
    const { session_token, user } = await api.verifyToken(token);
    setSessionToken(session_token);
    setState({ user, isLoading: false, isAuthenticated: true });
  }, [setSessionToken]);

  // Logout
  const logout = useCallback(async () => {
    const token = getSessionToken();
    if (token) {
      try {
        await api.logout(token);
      } catch {
        // Ignore logout errors - we'll clear local state anyway
      }
    }
    setSessionToken(null);
    setState({ user: null, isLoading: false, isAuthenticated: false });
  }, [getSessionToken, setSessionToken]);

  // Update user
  const updateUser = useCallback(async (data: { name?: string }) => {
    const token = getSessionToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    const { user } = await api.updateMe(token, data);
    setState(prev => ({ ...prev, user }));
  }, [getSessionToken]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    const token = getSessionToken();
    if (!token) {
      return;
    }
    try {
      const { user } = await api.getMe(token);
      setState(prev => ({ ...prev, user }));
    } catch {
      // Session expired
      setSessionToken(null);
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  }, [getSessionToken, setSessionToken]);

  const value: AuthContextValue = {
    ...state,
    login,
    verifyToken,
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
